import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { GraduationCap, Plus, Edit, Trash2, CheckCircle, XCircle, Save } from 'lucide-react';

const GestionSemestres = () => {
  const { API } = useContext(AuthContext);
  const [semestres, setSemestres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSemestre, setSelectedSemestre] = useState(null);
  const [createForm, setCreateForm] = useState({
    tipo: 'semestre_academico',  // Por defecto semestre académico
    numero: '1',  // Valor por defecto 1 para empezar
    nombre: '',
    descripcion: '',
    ano_lectivo: new Date().getFullYear().toString(),
    activo: true
  });
  const [editForm, setEditForm] = useState({
    nombre: '',
    descripcion: '',
    ano_lectivo: '',
    activo: true,
    fecha_inicio: '',
    fecha_fin: ''
  });

  useEffect(() => {
    loadSemestres();
  }, []);
  
  const loadClases = async () => {
    try {
      const response = await fetch(`${API}/clases`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClases(data);
      }
    } catch (error) {
      console.error('Error loading clases:', error);
    }
  };

  const loadSemestres = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/semestres/precios`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // La respuesta viene como { semestres: [...] }
        setSemestres(data.semestres || []);
      }
    } catch (error) {
      console.error('Error loading semestres:', error);
      toast.error('Error al cargar semestres');
    } finally {
      setLoading(false);
    }
  };

  const getNextSemestreNumber = () => {
    if (semestres.length === 0) return 1;
    
    // Encontrar el número más alto
    const numeros = semestres
      .map(s => s.semester_number || s.numero)
      .filter(n => n != null && !isNaN(n));
    
    if (numeros.length === 0) return 1;
    
    const maxNumero = Math.max(...numeros);
    return maxNumero + 1;
  };

  const getNextGrupoEspecialNumber = () => {
    // Grupos especiales empiezan desde 5 en adelante
    const numerosGrupos = semestres
      .map(s => s.semester_number || s.numero)
      .filter(n => n != null && !isNaN(n) && n >= 5);
    
    if (numerosGrupos.length === 0) return 5; // Primer grupo especial
    
    const maxNumero = Math.max(...numerosGrupos);
    return maxNumero + 1;
  };

  const handleCreate = async () => {
    if (!createForm.numero || !createForm.nombre) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/semestres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          numero: parseInt(createForm.numero),
          nombre: createForm.nombre,
          descripcion: createForm.descripcion || '',
          ano_lectivo: createForm.ano_lectivo || null,
          activo: createForm.activo
        })
      });

      if (response.ok) {
        toast.success('Semestre creado exitosamente');
        setIsCreateDialogOpen(false);
        setCreateForm({
          numero: '',
          nombre: '',
          descripcion: '',
          ano_lectivo: new Date().getFullYear().toString(),
          activo: true
        });
        loadSemestres();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear semestre');
      }
    } catch (error) {
      console.error('Error creating semestre:', error);
      toast.error('Error al crear semestre');
    }
  };

  const handleEdit = async () => {
    try {
      const response = await fetch(`${API}/admin/semestres/${selectedSemestre.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error updating semester:', error);
        const errorMsg = typeof error.detail === 'string' 
          ? error.detail 
          : 'Error al actualizar semestre';
        toast.error(errorMsg);
        return;
      }
      
      toast.success('Semestre actualizado exitosamente');
      setIsEditDialogOpen(false);
      loadSemestres();
    } catch (error) {
      console.error('Error updating semestre:', error);
      toast.error('Error al actualizar semestre');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/admin/semestres/${selectedSemestre.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Semestre eliminado exitosamente');
        setIsDeleteDialogOpen(false);
        loadSemestres();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar semestre');
      }
    } catch (error) {
      console.error('Error deleting semestre:', error);
      toast.error('Error al eliminar semestre');
    }
  };

  const handleActualizarFechas = async () => {
    try {
      setLoading(true);
      toast.info('Actualizando fechas de semestres...');
      
      const response = await fetch(`${API}/admin/semestres/actualizar-fechas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Fechas actualizadas exitosamente');
        loadSemestres();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar fechas');
      }
    } catch (error) {
      console.error('Error updating dates:', error);
      toast.error('Error al actualizar fechas');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = async (semestre) => {
    setSelectedSemestre(semestre);
    
    // Formatear fechas para input type="date" (YYYY-MM-DD)
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };
    
    setEditForm({
      nombre: semestre.name,
      descripcion: semestre.descripcion || '',
      ano_lectivo: semestre.year || '',
      activo: semestre.is_active,
      fecha_inicio: formatDateForInput(semestre.fecha_inicio || semestre.start_date),
      fecha_fin: formatDateForInput(semestre.fecha_fin || semestre.end_date)
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (semestre) => {
    setSelectedSemestre(semestre);
    setIsDeleteDialogOpen(true);
  };

  const getSemestreColor = (numero) => {
    if (!numero || numero === null) return 'bg-gray-500'; // Color por defecto si no hay número
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-orange-500'];
    return colors[(numero - 1) % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando semestres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-8 w-8" />
            Gestión de Semestres
          </h1>
          <p className="text-gray-600 mt-2">
            Crea y administra semestres ilimitados para el instituto académico
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleActualizarFechas}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            <Save className="h-4 w-4 mr-2" />
            Actualizar Fechas
          </Button>
          <Button
            onClick={() => {
              const nextNum = getNextSemestreNumber();
              setCreateForm({
                tipo: nextNum <= 4 ? 'semestre_academico' : 'grupo_especial',
                numero: nextNum.toString(),
                nombre: '',
                ano_lectivo: new Date().getFullYear().toString(),
                activo: true
              });
              setSelectedClases([]);
              setIsCreateDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear Semestre
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {semestres.map((semestre) => (
          <Card 
            key={semestre.id}
            className={`${semestre.is_active ? 'border-2 border-blue-500' : 'border-2 border-gray-300 opacity-70'}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${getSemestreColor(semestre.semestre || semestre.semester_number)} flex items-center justify-center text-white font-bold text-xl`}>
                    {semestre.semestre || semestre.semester_number || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{semestre.name}</h3>
                    {semestre.year && (
                      <p className="text-sm text-gray-600">Año: {semestre.year}</p>
                    )}
                  </div>
                </div>
                {semestre.is_active ? (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircle className="h-6 w-6 text-gray-400" />
                )}
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <Badge variant={semestre.is_active ? "default" : "secondary"}>
                  {semestre.is_active ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(semestre)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => openDeleteDialog(semestre)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {semestres.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <GraduationCap className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No hay semestres creados aún</p>
            <Button onClick={() => {
              setCreateForm({
                numero: '1',
                nombre: '',
                ano_lectivo: new Date().getFullYear().toString(),
                activo: true
              });
              setIsCreateDialogOpen(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Primer Semestre
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Semestre</DialogTitle>
            <DialogDescription>
              Agrega un nuevo semestre al sistema
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Categoría *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const nextNum = getNextSemestreNumber();
                    setCreateForm({
                      ...createForm, 
                      tipo: 'semestre_academico',
                      numero: nextNum <= 4 ? nextNum.toString() : '1'
                    });
                  }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    createForm.tipo === 'semestre_academico' || !createForm.tipo
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <div className="text-center">
                    <i className="fas fa-graduation-cap text-xl text-blue-600 mb-1"></i>
                    <div className="text-sm font-medium">Semestre Académico</div>
                    <div className="text-xs text-gray-500">(Números 1-4)</div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setCreateForm({
                      ...createForm, 
                      tipo: 'grupo_especial',
                      numero: getNextGrupoEspecialNumber().toString()
                    });
                  }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    createForm.tipo === 'grupo_especial'
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-300 hover:border-purple-300'
                  }`}
                >
                  <div className="text-center">
                    <i className="fas fa-users text-xl text-purple-600 mb-1"></i>
                    <div className="text-sm font-medium">Grupo Especial</div>
                    <div className="text-xs text-gray-500">(Desde número 5)</div>
                  </div>
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="numero">Número Asignado</Label>
              <Input
                id="numero"
                type="number"
                min="1"
                value={createForm.numero}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {createForm.tipo === 'grupo_especial' 
                  ? 'Grupos especiales comienzan desde 5' 
                  : 'Semestres académicos van del 1 al 4'}
              </p>
            </div>
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                placeholder="Ej: Quinto Semestre"
                value={createForm.nombre}
                onChange={(e) => setCreateForm({...createForm, nombre: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="descripcion_create">
                Descripción <span className="text-gray-400 text-xs font-normal">(opcional)</span>
              </Label>
              <textarea
                id="descripcion_create"
                data-testid="input-descripcion-create"
                placeholder="Ej: Semestre orientado a fundamentos bíblicos y liderazgo pastoral..."
                value={createForm.descripcion}
                onChange={(e) => setCreateForm({...createForm, descripcion: e.target.value})}
                rows={3}
                className="w-full mt-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                maxLength={500}
              />
              <p className="text-[11px] text-gray-500 mt-0.5">
                Este texto se mostrará al estudiante en el Paso 1 de Registración para ayudarlo a elegir.
              </p>
            </div>
            <div>
              <Label htmlFor="ano_lectivo">Año Lectivo</Label>
              <Input
                id="ano_lectivo"
                placeholder="Ej: 2025"
                value={createForm.ano_lectivo}
                onChange={(e) => setCreateForm({...createForm, ano_lectivo: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="activo_create"
                checked={createForm.activo}
                onChange={(e) => setCreateForm({...createForm, activo: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="activo_create">Semestre activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate}>
              <Save className="h-4 w-4 mr-2" />
              Crear Semestre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Semestre</DialogTitle>
            <DialogDescription>
              Modifica la información del semestre
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_nombre">Nombre</Label>
              <Input
                id="edit_nombre"
                value={editForm.nombre}
                onChange={(e) => setEditForm({...editForm, nombre: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit_descripcion">
                Descripción <span className="text-gray-400 text-xs font-normal">(opcional)</span>
              </Label>
              <textarea
                id="edit_descripcion"
                data-testid="input-descripcion-edit"
                placeholder="Descripción visible para el estudiante en el flujo de registración"
                value={editForm.descripcion}
                onChange={(e) => setEditForm({...editForm, descripcion: e.target.value})}
                rows={3}
                className="w-full mt-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                maxLength={500}
              />
              <p className="text-[11px] text-gray-500 mt-0.5">
                Este texto se muestra al estudiante en el Paso 1 de Registración.
              </p>
            </div>
            <div>
              <Label htmlFor="edit_ano_lectivo">Año Lectivo</Label>
              <Input
                id="edit_ano_lectivo"
                value={editForm.ano_lectivo}
                onChange={(e) => setEditForm({...editForm, ano_lectivo: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit_fecha_inicio">Fecha de Inicio</Label>
              <Input
                id="edit_fecha_inicio"
                type="date"
                value={editForm.fecha_inicio}
                onChange={(e) => setEditForm({...editForm, fecha_inicio: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit_fecha_fin">Fecha de Fin</Label>
              <Input
                id="edit_fecha_fin"
                type="date"
                value={editForm.fecha_fin}
                onChange={(e) => setEditForm({...editForm, fecha_fin: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="activo_edit"
                checked={editForm.activo}
                onChange={(e) => setEditForm({...editForm, activo: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="activo_edit">Semestre activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit}>
              <Save className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Semestre</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar este semestre?
            </DialogDescription>
          </DialogHeader>
          {selectedSemestre && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Semestre:</strong> {selectedSemestre.nombre}</p>
              <p><strong>Número:</strong> {selectedSemestre.numero}</p>
              {selectedSemestre.ano_lectivo && (
                <p><strong>Año:</strong> {selectedSemestre.ano_lectivo}</p>
              )}
            </div>
          )}
          <p className="text-red-600 text-sm">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionSemestres;
